import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Briefcase, MapPin, DollarSign, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SkeletonJobCard } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import { FadeIn } from '@/components/ui/AnimatedComponents';
import { useAuthStore } from '@/store/authStore';
import { ENV } from '@/config/env';
import type { Job } from '@/types';

export function ProviderJobsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [counties, setCounties] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const [daysPosted, setDaysPosted] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const jobsPerPage = 20;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProviderProfile();
    fetchProviderJobs();
  }, []);

  const fetchProviderProfile = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/profile`,
        {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-User-ID': user.id,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        
        // Load only provider's service areas and specializations
        if (data.profile_data?.serviceCounties) {
          fetchProviderCounties(data.profile_data.serviceCounties);
        }
        if (data.profile_data?.specializations) {
          fetchProviderCategories(data.profile_data.specializations);
        }
      }
    } catch (err) {
      // Failed to fetch provider profile
    }
  };

  const fetchProviderJobs = async () => {
    if (!user?.id) {
      setError('Please log in to view jobs');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch only NEW jobs (not quoted yet) that match provider's counties + categories
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/available`,
        {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-User-ID': user.id,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        setError('Failed to load jobs. Please try again.');
        return;
      }

      const data = await response.json();
      setJobs(data.data || []);
    } catch (err) {
      setError('Failed to load jobs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProviderCategories = async (categoryIds: string[]) => {
    try {
      // Fetch all categories and filter to provider's specializations
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/public/categories`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const data = await response.json();
        const allCategories = data.data || data;
        // Filter to only provider's specializations (excluding non-UUID values)
        const providerCategories = allCategories.filter((cat: any) => 
          categoryIds.includes(cat.id) && cat.id.length === 36
        );
        setCategories(providerCategories);
      }
    } catch (err) {
      // Failed to fetch categories
    }
  };

  const fetchProviderCounties = async (countyIds: string[]) => {
    try {
      // Fetch all counties and filter to provider's service areas
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/public/counties`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const data = await response.json();
        const allCounties = data.data || data;
        // Filter to only provider's service counties
        const providerCounties = allCounties.filter((county: any) => 
          countyIds.includes(county.id)
        );
        setCounties(providerCounties);
      }
    } catch (err) {
      // Failed to fetch counties
    }
  };

  const getFilteredJobs = () => {
    let filteredJobs = jobs;
    
    // Search filter
    if (searchTerm) {
      filteredJobs = filteredJobs.filter(job =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Category filter
    if (selectedCategory) {
      filteredJobs = filteredJobs.filter(job => job.category === selectedCategory);
    }
    
    // County filter
    if (selectedCounty) {
      filteredJobs = filteredJobs.filter(job => 
        job.location.toLowerCase().includes(selectedCounty.toLowerCase())
      );
    }
    
    // Urgency filter
    if (selectedUrgency) {
      filteredJobs = filteredJobs.filter(job => job.urgency === selectedUrgency);
    }
    
    // Days posted filter
    if (daysPosted) {
      const now = new Date();
      filteredJobs = filteredJobs.filter(job => {
        if (!job.createdAt) return true; // Include jobs without date
        const jobDate = new Date(job.createdAt);
        if (isNaN(jobDate.getTime())) return true; // Include invalid dates
        const daysDiff = Math.floor((now.getTime() - jobDate.getTime()) / (1000 * 60 * 60 * 24));
        switch (daysPosted) {
          case 'today': return daysDiff === 0;
          case 'week': return daysDiff <= 7;
          case 'month': return daysDiff <= 30;
          default: return true;
        }
      });
    }
    
    // Sort
    filteredJobs.sort((a, b) => {
      switch (sortBy) {
        case 'newest': {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        }
        case 'oldest': {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        }
        case 'budget-high': {
          const avgA = ((a.budgetMin || 0) + (a.budgetMax || 0)) / 2;
          const avgB = ((b.budgetMin || 0) + (b.budgetMax || 0)) / 2;
          return avgB - avgA;
        }
        case 'budget-low': {
          const avgA = ((a.budgetMin || 0) + (a.budgetMax || 0)) / 2;
          const avgB = ((b.budgetMin || 0) + (b.budgetMax || 0)) / 2;
          return avgA - avgB;
        }
        default:
          return 0;
      }
    });
    
    return filteredJobs;
  };

  const filteredJobsList = getFilteredJobs();
  const totalPages = Math.ceil(filteredJobsList.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const paginatedJobs = filteredJobsList.slice(startIndex, startIndex + jobsPerPage);

  // Helper functions
  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedCategory) count++;
    if (selectedCounty) count++;
    if (daysPosted) count++;
    if (selectedUrgency) count++;
    return count;
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedCounty('');
    setSelectedUrgency('');
    setDaysPosted('');
    setSortBy('newest');
  };

  const removeFilter = (filterType: string) => {
    switch (filterType) {
      case 'search': setSearchTerm(''); break;
      case 'category': setSelectedCategory(''); break;
      case 'county': setSelectedCounty(''); break;
      case 'days': setDaysPosted(''); break;
      case 'urgency': setSelectedUrgency(''); break;
    }
  };

  const getRelativeTime = (dateString: string | Date | undefined) => {
    if (!dateString) return 'Recently';
    
    const now = new Date();
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Recently';
    
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const activeFiltersCount = getActiveFiltersCount();

  const FilterSidebar = () => (
    <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
      {/* Sort By - Moved to Top */}
      <div className="pb-4 border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Sort by</label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="budget-high">Highest Budget</option>
          <option value="budget-low">Lowest Budget</option>
        </select>
      </div>

      {/* Search */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Keywords..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category {categories.length > 0 && <span className="text-xs text-gray-500">({categories.length} available)</span>}
        </label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* County */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          County {counties.length > 0 && <span className="text-xs text-gray-500">({counties.length} areas)</span>}
        </label>
        <select
          value={selectedCounty}
          onChange={(e) => setSelectedCounty(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="">All Counties</option>
          {counties.map(county => (
            <option key={county.id} value={county.name}>{county.name}</option>
          ))}
        </select>
      </div>

      {/* Days Posted */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Posted</label>
        <select
          value={daysPosted}
          onChange={(e) => setDaysPosted(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="">Any Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
        </select>
      </div>

      {/* Urgency */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Urgency</label>
        <select
          value={selectedUrgency}
          onChange={(e) => setSelectedUrgency(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="">All Urgency</option>
          <option value="urgent">Urgent</option>
          <option value="normal">Normal</option>
          <option value="flexible">Flexible</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Browse Jobs' }]} />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6 mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Available Jobs</h1>
            <p className="text-gray-600">Browse new jobs in your service area</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant="success">{filteredJobsList.length} Jobs</Badge>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Sidebar Filters */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            <Card className="sticky top-4">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge variant="success" size="sm">{activeFiltersCount}</Badge>
                  )}
                </h3>
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                    Clear All
                  </Button>
                )}
              </div>
              <div className="p-4">
                <FilterSidebar />
              </div>
            </Card>
          </div>

          {/* Mobile Filter Button */}
          <div className="lg:hidden">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setShowMobileFilters(true)}
              icon={<Filter className="w-4 h-4" />}
            >
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>
          </div>

          {/* Jobs List */}
          <div className="flex-1 min-w-0">
            {/* Active Filters Chips */}
            {activeFiltersCount > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {searchTerm && (
                  <Badge variant="neutral" className="flex items-center gap-2">
                    Search: {searchTerm}
                    <button onClick={() => removeFilter('search')} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedCategory && (
                  <Badge variant="neutral" className="flex items-center gap-2">
                    {selectedCategory}
                    <button onClick={() => removeFilter('category')} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedCounty && (
                  <Badge variant="neutral" className="flex items-center gap-2">
                    {selectedCounty}
                    <button onClick={() => removeFilter('county')} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {daysPosted && (
                  <Badge variant="neutral" className="flex items-center gap-2">
                    {daysPosted}
                    <button onClick={() => removeFilter('days')} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedUrgency && (
                  <Badge variant="neutral" className="flex items-center gap-2">
                    {selectedUrgency}
                    <button onClick={() => removeFilter('urgency')} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
              </div>
            )}

            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <SkeletonJobCard key={i} />
                ))}
              </div>
            ) : error ? (
              <Card padding="xl" className="text-center border-error-200 bg-error-50">
                <h3 className="text-lg font-semibold text-error-900 mb-2">Error Loading Jobs</h3>
                <p className="text-error-700 mb-4">{error}</p>
                <Button onClick={fetchProviderJobs} variant="outline">Try Again</Button>
              </Card>
            ) : paginatedJobs.length === 0 ? (
              <EnhancedEmptyState
                icon={Briefcase}
                title="No jobs found"
                description={
                  activeFiltersCount > 0 
                    ? "We couldn't find any jobs matching your criteria. Try adjusting your filters."
                    : "No jobs available in your service area at the moment. Check back soon!"
                }
                action={
                  activeFiltersCount > 0 
                    ? { label: 'Clear Filters', onClick: clearAllFilters, icon: <X className="w-4 h-4" /> }
                    : undefined
                }
                size="md"
                illustration="pattern"
              />
            ) : (
              <>
                <div className="space-y-3">
                  {paginatedJobs.map((job, index) => (
                    <FadeIn key={job.id} delay={index * 30}>
                      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-5 border border-gray-200 group cursor-pointer">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-3 mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                                    {job.title}
                                  </h3>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {getRelativeTime(job.createdAt)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                                  {job.description}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{job.location}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Briefcase className="w-4 h-4" />
                                <span>{job.category}</span>
                              </div>
                              {(job.budgetMin || job.budgetMax) && (
                                <div className="flex items-center gap-1">
                                  <DollarSign className="w-4 h-4" />
                                  <span>
                                    €{job.budgetMin || 0} - €{job.budgetMax || 0}
                                  </span>
                                </div>
                              )}
                              {job.urgency && (
                                <Badge 
                                  variant={job.urgency.toLowerCase() === 'high' ? 'error' : 'neutral'}
                                  size="sm"
                                >
                                  {job.urgency}
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="flex sm:flex-col gap-2 sm:items-end">
                            <Button 
                              size="sm" 
                              variant="primary"
                              onClick={() => navigate(`/jobs/${job.id}`)}
                              className="whitespace-nowrap"
                            >
                              View Details
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigate(`/jobs/${job.id}?action=quote`)}
                              className="whitespace-nowrap"
                            >
                              Quick Quote
                            </Button>
                          </div>
                        </div>
                      </div>
                    </FadeIn>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center mt-6 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex gap-1">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Privacy Note */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>🔒 Privacy Protected:</strong> Full addresses are hidden until your quote is accepted. You'll see the town/city and county to help you assess travel distance.
          </p>
        </div>
      </main>

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setShowMobileFilters(false)}>
          <div 
            className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="success" size="sm">{activeFiltersCount}</Badge>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                    Clear All
                  </Button>
                )}
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <FilterSidebar />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
              <Button
                fullWidth
                variant="primary"
                onClick={() => setShowMobileFilters(false)}
              >
                Show {filteredJobsList.length} Jobs
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
